import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CATALOGO_EQUIPAMENTOS,
  CATALOGO_EQUIPE_FISCALIZACAO,
  CATALOGO_MAO_OBRA,
  type DiarioItem,
  type DiarioResumo,
} from "../../../shared/diario";
import { api } from "../api/cliente";
import { chavesDiario, useDiarios } from "../api/diarios";
import { useAuth } from "../auth/AuthProvider";
import { useAvisos } from "../components/Avisos";
import { Carregando, ErroCarregamento } from "../components/ui";

const HOJE = new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });

/** Formata "YYYY-MM-DD" como dd/mm/aaaa sem cair no fuso de new Date(). */
function dataBr(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

function itensPadrao(): DiarioItem[] {
  const monta = (nomes: string[], tipo: DiarioItem["tipo"]): DiarioItem[] =>
    nomes.map((nome, ordem) => ({ tipo, nome, quantidade: 0, ordem }));
  return [
    ...monta(CATALOGO_EQUIPAMENTOS, "equipamento"),
    ...monta(CATALOGO_MAO_OBRA, "mao_obra"),
    ...monta(CATALOGO_EQUIPE_FISCALIZACAO, "equipe_fiscalizacao"),
  ];
}

const MESES = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];

export function Diarios() {
  const diarios = useDiarios();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { avisar, avisarErro } = useAvisos();
  const { usuario } = useAuth();
  const ehAdmin = usuario?.papel === "admin";
  const [confirmar, setConfirmar] = useState<DiarioResumo | null>(null);

  const criar = useMutation({
    mutationFn: () =>
      api<{ id: number }>("/api/diarios", {
        method: "POST",
        body: { data: new Date().toISOString().slice(0, 10), itens: itensPadrao() },
      }),
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: chavesDiario.lista });
      navigate(`/diarios/${id}`);
    },
    onError: avisarErro,
  });

  const excluir = useMutation({
    mutationFn: (id: number) => api(`/api/diarios/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      avisar("Diário excluído.");
      queryClient.invalidateQueries({ queryKey: chavesDiario.lista });
    },
    onError: avisarErro,
  });

  const proximoDia = useMutation({
    mutationFn: (baseId: number) => api<{ id: number }>(`/api/diarios/${baseId}/proximo-dia`, { method: "POST" }),
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: chavesDiario.lista });
      navigate(`/diarios/${id}`);
    },
    onError: avisarErro,
  });

  const lista = diarios.data ?? [];

  // Agrupa por obra; dentro de cada obra, ordena pelo nº de dias corridos (numero).
  const grupos = useMemo(() => {
    const map = new Map<string, DiarioResumo[]>();
    for (const d of lista) {
      const chave = d.obra?.trim() || "Obra não informada";
      if (!map.has(chave)) map.set(chave, []);
      map.get(chave)!.push(d);
    }
    const arr = [...map.entries()].map(([obra, ds]) => ({
      obra,
      diarios: ds.slice().sort((a, b) => (Number(a.numero) || 0) - (Number(b.numero) || 0)),
      ultima: ds.reduce((m, d) => (d.data > m ? d.data : m), ""),
      baseId: ds.reduce((best, d) => (d.data > best.data ? d : best), ds[0]).id,
    }));
    arr.sort((a, b) => b.ultima.localeCompare(a.ultima));
    return arr;
  }, [lista]);

  if (diarios.isLoading) return <Carregando />;
  if (diarios.error) return <ErroCarregamento erro={diarios.error} />;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 8 }}>
        <h1 style={{ margin: 0 }}>Diários de Obra</h1>
        <button className="btn primary" onClick={() => criar.mutate()} disabled={criar.isPending}>
          {criar.isPending ? "Criando…" : "+ Novo diário"}
        </button>
      </div>

      {lista.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 40 }}>
          <p className="note">Nenhum diário ainda. Clique em “+ Novo diário” para começar.</p>
        </div>
      ) : (
        grupos.map((g) => (
          <div key={g.obra} className="obra-grupo">
            <div className="obra-titulo">
              <span>🏗️ {g.obra} <span className="obra-contagem">({g.diarios.length})</span></span>
              <button
                className="btn primary obra-proximo"
                onClick={() => proximoDia.mutate(g.baseId)}
                disabled={proximoDia.isPending || (!ehAdmin && g.ultima.slice(0, 10) >= HOJE)}
                title={!ehAdmin && g.ultima.slice(0, 10) >= HOJE ? "Já existe diário de hoje — o próximo dia só poderá ser criado amanhã." : "Gerar o diário do próximo dia"}
              >
                {proximoDia.isPending ? "Gerando…" : "+ Próximo dia"}
              </button>
            </div>
            <div className="diario-lista">
              {g.diarios.map((d) => (
                <div key={d.id} className="card diario-card">
                  <Link to={`/diarios/${d.id}`} className="diario-card-link">
                    <div className="diario-data">
                      <span className="dia">{d.data.slice(8, 10)}</span>
                      <span className="mes">{MESES[Number(d.data.slice(5, 7)) - 1]}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong>{d.numero ? `${d.numero}º dia corrido` : "Sem dias corridos"}</strong>
                      <div className="diario-tags">
                        <span>{dataBr(d.data)}</span>
                        {d.total_fotos > 0 && <span>📷 {d.total_fotos}</span>}
                        {d.assinado && <span>✍️ Assinado</span>}
                        {d.criado_por_nome && <span>por {d.criado_por_nome}</span>}
                      </div>
                    </div>
                  </Link>
                  {ehAdmin || d.data.slice(0, 10) >= HOJE ? (
                    <button
                      className="btn danger diario-excluir"
                      title="Excluir diário"
                      onClick={() => setConfirmar(d)}
                    >
                      🗑
                    </button>
                  ) : (
                    <button className="btn diario-excluir" disabled title="Diários de dias anteriores só podem ser excluídos pelo administrador">🔒</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {confirmar && (
        <div className="diario-overlay" onClick={() => setConfirmar(null)}>
          <div className="diario-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Excluir diário</h2>
            <p className="note">
              Excluir o diário de <strong>{dataBr(confirmar.data)}</strong>
              {confirmar.obra ? <> — {confirmar.obra}</> : null}? Esta ação não pode ser desfeita.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button className="btn" onClick={() => setConfirmar(null)}>Cancelar</button>
              <button
                className="btn danger"
                disabled={excluir.isPending}
                onClick={() => { const id = confirmar.id; setConfirmar(null); excluir.mutate(id); }}
              >
                {excluir.isPending ? "Excluindo…" : "Excluir"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
